/**
 * Public course catalog at `/courses` — every published course across
 * every trainer. Magazine-grid layout with featured at top.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { FilmStripIcon as FilmStrip, SparkleIcon as Sparkle, GraduationCapIcon as GraduationCap } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};
const $ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function CourseCatalog() {
  const [courses, setCourses] = useState(null);
  useEffect(() => {
    api.get("/trainer-courses")
      .then((r) => setCourses(r.data.courses || []))
      .catch(() => setCourses([]));
  }, []);
  // Native app: catalog sells paid digital video courses — App Review
  // 3.1.1 forbids the storefront in the shell.
  if (isNativeApp()) return <NativePlanNotice />;
  return (
    <div className="min-h-screen bg-[#FAFAFA] font-manrope text-ink">
      <Helmet>
        <title>Training Courses — Armstrong K9 Registry</title>
        <meta name="description" content="Browse video training courses from verified Armstrong K9 trainers. Puppy basics, leash manners, recall, behavior modification, and more." />
      </Helmet>
      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="catalog-brand">
            <img src="/logo-256.png" alt="" className="h-8 w-8 rounded-full object-contain" />
            <span className="font-spectral text-base tracking-tight">Armstrong K9 Registry</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-5 text-sm text-[#3F3F3F]">
            <Link to="/find-pros" className="hover:text-ink">Find pros</Link>
            <Link to="/media" className="hover:text-ink">Media</Link>
            <Link to="/login" className="hover:text-ink">Sign in</Link>
          </nav>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-8 md:pt-12 pb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#6B6B6B] hover:text-ink transition-colors mb-6"
          data-testid="courses-back-link"
        >
          ← Back to Armstrong K9 Registry
        </Link>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Training Courses</p>
        <h1 className="font-spectral text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 max-w-3xl">
          Learn from verified Armstrong K9 trainers — at your own pace.
        </h1>
        <p className="text-base text-[#3F3F3F] mt-5 max-w-2xl leading-relaxed">
          Pre-recorded video courses from background-checked professionals. Buy once, watch forever — or unlock the entire library with a Buyer Pass.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {courses === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="h-64 bg-white border border-[#E5E5E5] rounded-2xl animate-pulse" />)}
          </div>
        ) : courses.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center" data-testid="catalog-empty">
            <FilmStrip size={32} className="mx-auto text-[#9b9b9b]" />
            <p className="mt-3 font-spectral text-xl">No courses published yet.</p>
            <p className="mt-1 text-sm text-[#666] max-w-md mx-auto">
              Our trainers are recording new courses now. Check back soon.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-grid">
            {courses.map((c) => (
              <Link
                key={c.id}
                to={`/courses/${c.slug}`}
                className="group block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`catalog-card-${c.slug}`}
              >
                <div className="relative aspect-video bg-[#F4F4F4] overflow-hidden">
                  {c.cover_url ? (
                    <img src={resolveUrl(c.cover_url)} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0E0E0E] to-[#1f1f1f] grid place-items-center"><GraduationCap size={48} className="text-gold" /></div>
                  )}
                  <span className="absolute top-3 right-3 bg-black/80 text-white text-xs font-semibold rounded-full px-2.5 py-1">{$(c.flat_price_cents)}</span>
                </div>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-bold">{c.provider_name}</p>
                  <h3 className="font-spectral text-lg mt-1 leading-tight group-hover:text-[#C1272D] transition-colors">{c.title}</h3>
                  {c.subtitle && <p className="text-sm text-[#666] mt-1.5 line-clamp-2">{c.subtitle}</p>}
                  <p className="text-xs text-[#888] mt-2">{c.lesson_count} lesson{c.lesson_count === 1 ? "" : "s"}{c.purchase_count > 0 && ` · ${c.purchase_count} learner${c.purchase_count === 1 ? "" : "s"}`}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
